import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { Users, CheckCircle, Key, Wifi } from 'lucide-react';

const AdminUserManagement = () => {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSftpModal, setShowSftpModal] = useState(false);
  const [sftpCredentials, setSftpCredentials] = useState(null);
  const [sftpLoading, setSftpLoading] = useState(null); // user_id being processed
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [filters, setFilters] = useState({
    tenantId: searchParams.get('tenant') || '',
    role: ''
  });

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    role: user?.tenant_id === null ? 'admin' : 'vendor_user',
    tenant_id: user?.tenant_id || null,
    company_name: ''
  });

  const [passwordData, setPasswordData] = useState({
    new_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters.';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter.';
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number.';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) return 'Password must contain at least one special character (e.g. @, #, $, !).';
    return '';
  };

  const fetchData = async () => {
    if (!user) return;
    try {
      setLoading(true);

      const isSuperAdmin = user.tenant_id === null || user.tenant_id === undefined;

      if (isSuperAdmin) {
        const [usersRes, tenantsRes] = await Promise.all([
          adminAPI.users.list(0, 100, filters.tenantId || null, filters.role || null),
          adminAPI.tenants.list()
        ]);
        setUsers(usersRes.data);
        setTenants(tenantsRes.data);
        setError(null);
      } else {
        const usersRes = await adminAPI.users.list(0, 100, filters.tenantId || null, filters.role || null);
        setUsers(usersRes.data);
        setTenants([]);
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleInputChange = (e) => {
    const value = e.target.name === 'tenant_id'
      ? (e.target.value ? parseInt(e.target.value) : null)
      : e.target.value;

    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!editingUser) {
      const pwdErr = validatePassword(formData.password);
      if (pwdErr) { setPasswordError(pwdErr); return; }
    }
    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.password;
        await adminAPI.users.update(editingUser.user_id, updateData);
      } else {
        await adminAPI.users.create(formData);
      }
      setShowCreateModal(false);
      setEditingUser(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetPasswordError('');
    const pwdErr = validatePassword(passwordData.new_password);
    if (pwdErr) { setResetPasswordError(pwdErr); return; }
    try {
      await adminAPI.users.resetPassword(selectedUser.user_id, passwordData);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordData({ new_password: '' });
      alert('Password reset successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      role: user?.tenant_id === null ? 'admin' : 'vendor_user',
      tenant_id: user?.tenant_id || null,
      company_name: ''
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      tenant_id: user.tenant_id,
      company_name: user.company_name || ''
    });
    setShowCreateModal(true);
  };


  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleEnableSftp = async (targetUser) => {
    setSftpLoading(targetUser.user_id);
    try {
      const res = await adminAPI.users.enableSftp(targetUser.user_id);
      setSftpCredentials({ ...res.data.credentials, username: targetUser.username });
      setShowSftpModal(true);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to enable SFTP access');
    } finally {
      setSftpLoading(null);
    }
  };

  const handleResetSftpPassword = async (targetUser) => {
    setSftpLoading(targetUser.user_id);
    try {
      const res = await adminAPI.users.resetSftpPassword(targetUser.user_id);
      setSftpCredentials({ ...res.data.credentials, username: targetUser.username });
      setShowSftpModal(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset SFTP password');
    } finally {
      setSftpLoading(null);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'auditor': return 'role-auditor';
      case 'vendor_user': return 'role-vendor';
      default: return '';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px;
      margin-bottom: 32px;
    }

    .filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 24px;
    }

    .btn-primary {
      padding: 12px 24px;
      background: var(--gradient-primary);
      color: white;
      border: none;
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: var(--shadow-glow);
      position: relative;
      overflow: hidden;
    }

    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.4);
    }

    .btn-primary:active {
      transform: translateY(0);
    }

    .btn-secondary {
      padding: 12px 24px;
      background: transparent;
      color: hsl(var(--foreground));
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
      position: relative;
      overflow: hidden;
    }

    .btn-secondary:hover {
      background: hsl(var(--secondary));
      border-color: hsl(var(--primary));
      color: hsl(var(--primary));
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .btn-secondary:active {
      transform: translateY(0);
    }

    .filters {
      display: flex;
      gap: 16px;
      align-items: center;
      flex-wrap: wrap;
    }

    .filter-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .filter-group label {
      font-size: 14px;
      font-weight: 500;
      color: hsl(var(--muted-foreground));
    }

    .filter-group select {
      padding: 8px 16px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      font-weight: 500;
      color: hsl(var(--foreground));
      background: hsl(var(--background));
      cursor: pointer;
      outline: none;
      transition: all 0.2s;
    }

    .filter-group select:hover {
      border-color: hsl(var(--primary));
    }

    .content-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      overflow: hidden;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .data-table thead {
      background: hsl(var(--secondary) / 0.5);
    }

    .data-table th {
      padding: 12px 10px;
      text-align: left;
      font-size: 11px;
      font-weight: 700;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .data-table tbody tr {
      border-bottom: 1px solid hsl(var(--border));
      transition: background 0.2s;
    }

    .data-table tbody tr:hover {
      background: hsl(var(--secondary) / 0.3);
    }

    .data-table td {
      padding: 12px 10px;
      font-size: 13px;
      color: hsl(var(--foreground));
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .role-badge {
      padding: 4px 12px;
      border-radius: var(--radius-full);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .role-admin {
      background: hsl(var(--primary) / 0.15);
      color: hsl(var(--primary));
    }

    .role-auditor {
      background: hsl(var(--warning) / 0.15);
      color: hsl(var(--warning));
    }

    .role-vendor {
      background: hsl(var(--success) / 0.15);
      color: hsl(var(--success));
    }

    .status-inactive {
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .actions-cell {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .btn-icon {
      width: 36px;
      height: 36px;
      border: none;
      border-radius: var(--radius-md);
      font-size: 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .btn-password {
      background: hsl(var(--primary) / 0.15);
      color: hsl(var(--primary));
    }

    .btn-password:hover {
      background: hsl(var(--primary) / 0.25);
      transform: scale(1.05);
    }

    .btn-edit {
      background: hsl(var(--warning) / 0.15);
      color: hsl(var(--warning));
    }

    .btn-edit:hover {
      background: hsl(var(--warning) / 0.25);
      transform: scale(1.05);
    }

    .btn-delete {
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .btn-delete:hover {
      background: hsl(var(--destructive) / 0.25);
      transform: scale(1.05);
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
    }

    .empty-icon {
      font-size: 48px;
      opacity: 0.3;
      margin-bottom: 16px;
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      backdrop-filter: blur(8px);
    }

    .modal-content {
      background: hsl(var(--background));
      border-radius: var(--radius-lg);
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    }

    .modal-content.large {
      max-width: 700px;
    }

    .modal-content.small {
      max-width: 450px;
    }

    .modal-header {
      padding: 24px;
      border-bottom: 2px solid hsl(var(--border));
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: hsl(var(--secondary) / 0.3);
    }

    .modal-title {
      font-size: 20px;
      font-weight: 700;
      color: hsl(var(--foreground));
      margin: 0;
    }

    .btn-close {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-full);
      border: none;
      background: hsl(var(--secondary));
      color: hsl(var(--foreground));
      font-size: 20px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;
    }

    .btn-close:hover {
      transform: rotate(90deg) scale(1.1);
      background: hsl(var(--destructive) / 0.15);
      color: hsl(var(--destructive));
    }

    .modal-form {
      padding: 24px;
    }

    .modal-description {
      margin-bottom: 20px;
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      margin-bottom: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-group label {
      font-size: 13px;
      font-weight: 600;
      color: hsl(var(--foreground));
    }

    .form-group input,
    .form-group select,
    .form-group textarea {
      padding: 10px 14px;
      border: 2px solid hsl(var(--border));
      border-radius: var(--radius-md);
      font-size: 14px;
      color: hsl(var(--foreground));
      background: hsl(var(--background));
      transition: all 0.2s;
    }

    .form-group input:focus,
    .form-group select:focus,
    .form-group textarea:focus {
      outline: none;
      border-color: hsl(var(--primary));
      box-shadow: 0 0 0 3px hsl(var(--primary) / 0.1);
    }

    .form-group input:disabled {
      background: hsl(var(--secondary) / 0.5);
      cursor: not-allowed;
      opacity: 0.6;
    }

    .info-box {
      padding: 16px;
      background: hsl(var(--primary) / 0.1);
      border: 1px solid hsl(var(--primary) / 0.3);
      border-radius: var(--radius-md);
      margin-bottom: 20px;
    }

    .info-box strong {
      color: hsl(var(--primary));
    }

    .info-box ul {
      margin: 8px 0 0 20px;
      padding: 0;
    }

    .info-box li {
      font-size: 13px;
      color: hsl(var(--muted-foreground));
      margin: 4px 0;
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding-top: 20px;
      border-top: 1px solid hsl(var(--border));
    }

    .loading-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: hsl(var(--background));
    }

    .error-alert {
      padding: 16px 20px;
      background: hsl(var(--destructive) / 0.15);
      border: 1px solid hsl(var(--destructive) / 0.3);
      border-radius: var(--radius-lg);
      color: hsl(var(--destructive));
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .btn-sftp {
      background: hsl(200, 80%, 50%, 0.15);
      color: hsl(200, 80%, 45%);
    }
    .btn-sftp:hover {
      background: hsl(200, 80%, 50%, 0.25);
      transform: scale(1.05);
    }
    .btn-sftp-reset {
      background: hsl(160, 60%, 40%, 0.15);
      color: hsl(160, 60%, 35%);
    }
    .btn-sftp-reset:hover {
      background: hsl(160, 60%, 40%, 0.25);
      transform: scale(1.05);
    }
    .sftp-enabled-badge {
      padding: 3px 10px;
      border-radius: var(--radius-full);
      font-size: 11px;
      font-weight: 600;
      background: hsl(200, 80%, 50%, 0.15);
      color: hsl(200, 80%, 45%);
    }
    .sftp-disabled-badge {
      padding: 3px 10px;
      border-radius: var(--radius-full);
      font-size: 11px;
      font-weight: 600;
      background: hsl(var(--muted) / 0.3);
      color: hsl(var(--muted-foreground));
    }
    .credentials-box {
      background: hsl(var(--secondary) / 0.5);
      border: 1px solid hsl(var(--border));
      border-radius: var(--radius-md);
      padding: 16px;
      margin: 16px 0;
      font-family: monospace;
      font-size: 13px;
    }
    .credentials-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 0;
      border-bottom: 1px solid hsl(var(--border) / 0.5);
    }
    .credentials-row:last-child { border-bottom: none; }
    .credentials-label {
      font-weight: 600;
      color: hsl(var(--muted-foreground));
      min-width: 110px;
    }
    .credentials-value {
      color: hsl(var(--foreground));
      word-break: break-all;
      text-align: right;
    }
    .password-value {
      color: hsl(var(--primary));
      font-weight: 700;
      font-size: 15px;
    }
    .warning-box {
      padding: 12px 16px;
      background: hsl(var(--warning) / 0.1);
      border: 1px solid hsl(var(--warning) / 0.3);
      border-radius: var(--radius-md);
      font-size: 13px;
      color: hsl(var(--warning));
      margin-bottom: 16px;
    }
  `;

  if (loading && users.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading-container">
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
            <div style={{ fontSize: '14px', color: 'hsl(var(--muted-foreground))' }}>
              Loading users...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <h1 className="page-title">
            <Users size={28} />
            User Management
          </h1>
          <p className="page-subtitle">
            {user?.tenant_id === null
              ? 'Manage tenant administrators'
              : `Manage auditors and vendors for ${user?.tenant_name || 'your organization'}`}
          </p>
        </div>

        {error && (
          <div className="error-alert">
            <span style={{ fontSize: '20px' }}>⚠️</span>
            {error}
          </div>
        )}

        {/* Stats */}
        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container">
              <Users size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--foreground))', margin: '16px 0 4px' }}>
              {users.length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Users
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="stat-card">
            <div className="icon-container" style={{ background: 'var(--gradient-success)' }}>
              <CheckCircle size={20} />
            </div>
            <h3 style={{ fontSize: '32px', fontWeight: '800', color: 'hsl(var(--success))', margin: '16px 0 4px' }}>
              {users.filter(u => u.is_active).length}
            </h3>
            <p style={{ fontSize: '13px', fontWeight: '600', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Active Users
            </p>
          </motion.div>
        </motion.div>

        {/* Filters & Action Bar */}
        <div className="filter-bar">
          <div className="filters">
            {user?.tenant_id === null && (
              <div className="filter-group">
                <label>Filter by Tenant:</label>
                <select name="tenantId" value={filters.tenantId} onChange={handleFilterChange}>
                  <option value="">All Tenants</option>
                  {tenants.map(tenant => (
                    <option key={tenant.tenant_id} value={tenant.tenant_id}>
                      {tenant.tenant_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="filter-group">
              <label>Filter by Role:</label>
              <select name="role" value={filters.role} onChange={handleFilterChange}>
                <option value="">All Roles</option>
                {user?.tenant_id === null ? (
                  <option value="admin">Admin</option>
                ) : (
                  <>
                    <option value="auditor">Auditor</option>
                    <option value="vendor_user">Vendor User</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setEditingUser(null);
              resetForm();
              setShowCreateModal(true);
            }}
          >
            Create New User
          </button>
        </div>

        {/* Users Table */}
        <div className="content-card">
          {users.length === 0 ? (
            <div className="empty-state">
              <Users size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
              <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '16px' }}>No users found</p>
              <button
                className="btn-secondary"
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First User
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '3%' }}>ID</th>
                  <th style={{ width: '9%' }}>Username</th>
                  <th style={{ width: '11%' }}>Full Name</th>
                  <th style={{ width: '18%' }}>Email</th>
                  <th style={{ width: '8%' }}>Role</th>
                  <th style={{ width: '9%' }}>Tenant</th>
                  <th style={{ width: '9%' }}>Company</th>
                  <th style={{ width: '7%' }}>Status</th>
                  <th style={{ width: '7%' }}>SFTP</th>
                  <th style={{ width: '9%' }}>Last Login</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.user_id}>
                    <td style={{ fontWeight: '600', color: 'hsl(var(--muted-foreground))' }}>
                      {index + 1}
                    </td>
                    <td title={user.username}><strong>{user.username}</strong></td>
                    <td title={user.full_name}>{user.full_name}</td>
                    <td title={user.email} style={{ color: 'hsl(var(--muted-foreground))' }}>{user.email}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {user.role === 'vendor_user' ? 'Vendor' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td>{user.tenant_name || '-'}</td>
                    <td>{user.company_name || '-'}</td>
                    <td>
                      {user.is_active ? (
                        <span className="status-active" style={{ cursor: 'pointer' }} title="Click to deactivate" onClick={async () => {
                          if (window.confirm(`Are you sure you want to deactivate ${user.username}?`)) {
                            try {
                              await adminAPI.users.update(user.user_id, { is_active: false });
                              fetchData();
                            } catch (err) { setError(err.response?.data?.detail || 'Failed to deactivate user'); }
                          }
                        }}>Active</span>
                      ) : (
                        <span className="status-inactive" style={{ cursor: 'pointer' }} title="Click to activate" onClick={async () => {
                          if (window.confirm(`Are you sure you want to activate ${user.username}?`)) {
                            try {
                              await adminAPI.users.update(user.user_id, { is_active: true });
                              fetchData();
                            } catch (err) { setError(err.response?.data?.detail || 'Failed to activate user'); }
                          }
                        }}>Inactive</span>
                      )}
                    </td>
                    <td>
                      {user.role === 'vendor_user' ? (
                        user.sftp_enabled
                          ? <span className="sftp-enabled-badge">✓ Enabled</span>
                          : <span className="sftp-disabled-badge">— Off</span>
                      ) : (
                        <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '12px' }}>N/A</span>
                      )}
                    </td>
                    <td>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn-icon btn-password"
                          onClick={() => openPasswordModal(user)}
                          title="Reset Password"
                        >
                          <Key size={16} />
                        </button>
                        {user.role === 'vendor_user' && (
                          user.sftp_enabled ? (
                            <button
                              className="btn-icon btn-sftp-reset"
                              onClick={() => handleResetSftpPassword(user)}
                              title="Reset SFTP Key"
                              disabled={sftpLoading === user.user_id}
                            >
                              {sftpLoading === user.user_id ? '⏳' : <Wifi size={16} />}
                            </button>
                          ) : (
                            <button
                              className="btn-icon btn-sftp"
                              onClick={() => handleEnableSftp(user)}
                              title="Enable SFTP Access"
                              disabled={sftpLoading === user.user_id}
                            >
                              {sftpLoading === user.user_id ? '⏳' : <Wifi size={16} />}
                            </button>
                          )
                        )}
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(user)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit User Modal */}
        {showCreateModal && (
          <div className="modal-overlay">
            <div className="modal-content large">
              <div className="modal-header">
                <h2 className="modal-title">{editingUser ? '✏️ Edit User' : '➕ Create New User'}</h2>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="john_doe"
                      required
                      disabled={editingUser}
                    />
                  </div>
                  {!editingUser && (
                    <div className="form-group">
                      <label>Password *</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={(e) => { handleInputChange(e); setPasswordError(''); }}
                        placeholder="Min 8 chars, uppercase, number, special char"
                        required={!editingUser}
                      />
                      {passwordError && (
                        <div style={{ color: 'hsl(var(--destructive))', fontSize: '12px', marginTop: '4px' }}>
                          ⚠️ {passwordError}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1-555-0123"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    {editingUser ? (
                      <input
                        type="text"
                        value={formData.role === 'vendor_user' ? 'Vendor User' : formData.role === 'admin' ? 'Admin' : formData.role.charAt(0).toUpperCase() + formData.role.slice(1)}
                        disabled
                        style={{ cursor: 'not-allowed', background: 'hsl(var(--secondary) / 0.5)' }}
                      />
                    ) : (
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                      >
                        {user?.tenant_id === null ? (
                          <option value="admin">Admin (Tenant Admin)</option>
                        ) : (
                          <>
                            <option value="auditor">Auditor</option>
                            <option value="vendor_user">Vendor User</option>
                          </>
                        )}
                      </select>
                    )}
                  </div>
                  {user?.tenant_id === null && (
                    <div className="form-group">
                      <label>Tenant *</label>
                      <select
                        name="tenant_id"
                        value={formData.tenant_id || ''}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Tenant</option>
                        {tenants.map(tenant => (
                          <option key={tenant.tenant_id} value={tenant.tenant_id}>
                            {tenant.tenant_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {user?.tenant_id !== null && (
                    <div className="form-group">
                      <label>Tenant</label>
                      <input
                        type="text"
                        value={user?.tenant_name || 'Your Organization'}
                        disabled
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Company Name</label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="ABC Hospital"
                    />
                  </div>
                </div>

                <div className="info-box">
                  <strong>ℹ️ Role Rules:</strong>
                  {user?.tenant_id === null ? (
                    <ul>
                      <li><strong>Admin (Tenant Admin):</strong> Must be assigned to a tenant to manage that tenant's users</li>
                      <li>Super Admins can only create Tenant Admins, not Vendors or Auditors</li>
                    </ul>
                  ) : (
                    <ul>
                      <li><strong>Auditor:</strong> Can review all batches in {user?.tenant_name || 'your organization'}</li>
                      <li><strong>Vendor User:</strong> Can upload and manage their own batches</li>
                      <li>All users you create will be automatically assigned to {user?.tenant_name || 'your organization'}</li>
                    </ul>
                  )}
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="modal-overlay" onClick={() => { setShowPasswordModal(false); setResetPasswordError(''); }}>
            <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  <Key size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  Reset Password
                </h2>
                <button className="btn-close" onClick={() => setShowPasswordModal(false)}>✕</button>
              </div>
              <form onSubmit={handlePasswordReset} className="modal-form">
                <p className="modal-description">
                  Reset password for: <strong>{selectedUser?.username}</strong>
                </p>
                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => { setPasswordData({ new_password: e.target.value }); setResetPasswordError(''); }}
                    placeholder="Min 8 chars, uppercase, number, special char"
                    required
                  />
                  {resetPasswordError && (
                    <div style={{ color: 'hsl(var(--destructive))', fontSize: '12px', marginTop: '4px' }}>
                      ⚠️ {resetPasswordError}
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* SFTP Credentials Modal */}
        {showSftpModal && sftpCredentials && (
          <div className="modal-overlay" onClick={() => { setShowSftpModal(false); setSftpCredentials(null); }}>
            <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">
                  <Wifi size={20} style={{ display: 'inline', marginRight: '8px' }} />
                  SFTP Credentials
                </h2>
                <button className="btn-close" onClick={() => { setShowSftpModal(false); setSftpCredentials(null); }}>✕</button>
              </div>
              <div className="modal-form">
                <div className="warning-box">
                  ⚠️ <strong>Download the private key now.</strong> It will not be shown again.
                </div>
                <div className="credentials-box">
                  <div className="credentials-row">
                    <span className="credentials-label">Host</span>
                    <span className="credentials-value">{sftpCredentials.host}</span>
                  </div>
                  <div className="credentials-row">
                    <span className="credentials-label">Port</span>
                    <span className="credentials-value">22</span>
                  </div>
                  <div className="credentials-row">
                    <span className="credentials-label">Username</span>
                    <span className="credentials-value">{sftpCredentials.sftp_username}</span>
                  </div>
                  <div className="credentials-row">
                    <span className="credentials-label">Auth Type</span>
                    <span className="credentials-value">SSH Key File</span>
                  </div>
                  <div className="credentials-row">
                    <span className="credentials-label">Upload Path</span>
                    <span className="credentials-value">{sftpCredentials.upload_path}</span>
                  </div>
                </div>
                <div style={{ margin: '12px 0' }}>
                  <button
                    className="btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => {
                      const blob = new Blob([sftpCredentials.private_key_pem], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${sftpCredentials.sftp_username}.pem`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    ⬇ Download Private Key (.pem)
                  </button>
                </div>
                <div className="info-box">
                  <strong>📂 FileZilla Setup:</strong>
                  <ul>
                    <li>Open FileZilla → <strong>Site Manager → New Site</strong></li>
                    <li>Protocol: <strong>SFTP</strong>, Host: <code>{sftpCredentials.host}</code>, Port: <strong>22</strong></li>
                    <li>Logon Type: <strong>Key file</strong>, User: <code>{sftpCredentials.sftp_username}</code></li>
                    <li>Key file: select the downloaded <code>.pem</code> file</li>
                  </ul>
                  <strong>📂 Upload Folder Structure:</strong>
                  <ul>
                    <li>Create a folder for each job: <code>{'/{job_code}/'}</code></li>
                    <li>Inside job folder, create a batch folder: <code>{'/{batch_name}/'}</code></li>
                    <li>Inside batch folder, create <code>/artifacts/</code> and upload TIFFs there</li>
                    <li>Example: <code>/JOB001/batch-jan/artifacts/img001.tif</code></li>
                  </ul>
                </div>
                <div className="modal-actions">
                  <button
                    className="btn-primary"
                    onClick={() => { setShowSftpModal(false); setSftpCredentials(null); }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </DashboardLayout>
    </>
  );
};

export default AdminUserManagement;
